import { Add, CloudUpload } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DocumentFileCard from "components/documents/DocumentFileCard";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import { useDeleteDocument, useGetDocuments, useUpsertDocument } from "core/documents/hooks";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { erroredAxiosResponse } from "utils";

type Props = {
  tempWorker: any;
};

const FreeFormDocuments = ({ tempWorker }: Props) => {
  const tempWorkerId = tempWorker.id;
  const { confirmationDialog, confirm, closeConfirmationDialog } = useConfirmationDialog();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("");
  const [isCreateSubmitted, setIsCreateSubmitted] = useState(false);
  const { data: documentsData } = useGetDocuments(
    tempWorkerId,
    { onError: () => toast.error("une erreur est survenue") },
  );

  const freeFormDocuments = useMemo(
    () => documentsData?.documents?.filter((document: any) => document.key === "free_form") ?? [],
    [documentsData?.documents],
  );

  const upsertDocument = useUpsertDocument(tempWorkerId, {
    onSuccess: () => toast.success("Le fichier a été uploadé"),
    onError: (responseError: any) => toast.error(erroredAxiosResponse(responseError, "Le document n'a pas pu être uploadé")),
  });

  const deleteFileMutation = useDeleteDocument(tempWorkerId, {
    onSuccess: () => {
      toast.success("Le fichier a été supprimé");
    },
    onError: (responseError: any) => toast.error(erroredAxiosResponse(responseError, "Le document n'a pas pu être supprimé")),
  });

  const createDocumentMutation = useUpsertDocument(tempWorkerId, {
    onSuccess: () => {
      toast.success("Le document complémentaire a été créé");
      setCreateModalOpen(false);
      setDocumentTitle("");
      setIsCreateSubmitted(false);
    },
    onError: (responseError: any) => toast.error(erroredAxiosResponse(responseError, "Le document n'a pas pu être créé")),
  });

  const trimmedDocumentTitle = documentTitle.trim();
  const titleError = isCreateSubmitted && trimmedDocumentTitle.length === 0;

  const openCreateModal = () => {
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setDocumentTitle("");
    setIsCreateSubmitted(false);
  };

  const createFreeFormDocument = () => {
    setIsCreateSubmitted(true);
    if (!trimmedDocumentTitle) return;

    createDocumentMutation.mutate({
      tempWorkerId,
      key: "free_form",
      name: trimmedDocumentTitle,
    });
  };

  const onDeleteFile = useCallback((documentId: number, file: any) => {
    confirm({
      title: "Supprimer le document",
      description: "Voulez-vous vraiment supprimer le document ?",
      onConfirm: () => {
        closeConfirmationDialog();
        deleteFileMutation.mutate({ tempWorkerId, id: documentId, file_id: file.id });
      },
    });
  }, [closeConfirmationDialog, confirm, deleteFileMutation, tempWorkerId]);

  const handleFileChange = useCallback((document: any) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    upsertDocument.mutate({
      tempWorkerId,
      documentId: document.id,
      key: "free_form",
      file,
    });
    e.target.value = "";
  }, [tempWorkerId, upsertDocument]);

  const fileLabel = (index: number) => {
    switch (index) {
      case 0:
        return "Recto";
      case 1:
        return "Verso";
      default:
        return 'Autre';
    }
  };

  return (
    <Box>
      {confirmationDialog}
      <Box
        sx={{
          backgroundColor: "primary.100",
          padding: "1rem",
          border: "1px dashed",
          borderColor: "primary.300",
          borderRadius: "0.5rem",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h6">Documents</Typography>
          <Button
            variant="contained"
            color="secondary"
            sx={{ padding: "1rem", height: "40px", fontSize: "0.875rem" }}
            onClick={openCreateModal}
            disabled={createDocumentMutation.isLoading}
            startIcon={<Add />}
          >
            Ajouter un nouveau document complémentaire
          </Button>
        </Stack>

        {freeFormDocuments.length === 0 ? (
          <Typography variant="body1" sx={{ color: "grey.700" }}>
            Aucun document complémentaire pour le moment.
          </Typography>
        ) : (
          <Stack gap={3}>
            {freeFormDocuments.map((document: any) => (
              <Box key={document.id} sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.125rem" }}>{document.name || "Document complémentaire"}</Typography>
                  <label htmlFor={`upload-free-form-file-${document.id}`}>
                    <input
                      style={{ display: "none" }}
                      id={`upload-free-form-file-${document.id}`}
                      type="file"
                      disabled={upsertDocument.isLoading}
                      onChange={handleFileChange(document)}
                    />
                    <Stack direction="row" gap={1} sx={{ cursor: "pointer", alignItems: "center" }}>
                      {upsertDocument.isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <>
                          <CloudUpload sx={{ color: "secondary.main", fontSize: "20px" }} />
                          <Typography variant="body1" sx={{ color: "secondary.main", textDecoration: "underline" }}>
                            ajouter un fichier
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </label>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                  {document.files.map((file: any, index: number) => (
                    <DocumentFileCard
                      key={file.id}
                      file={file}
                      document={document}
                      label={fileLabel(index)}
                      kind="free_form"
                      onDeleteFile={(currentFile: any) => onDeleteFile(document.id, currentFile)}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      <Dialog open={createModalOpen} onClose={createDocumentMutation.isLoading ? undefined : closeCreateModal} fullWidth maxWidth="sm">
        <DialogTitle>Ajouter un nouveau document complémentaire</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Titre du document"
            value={documentTitle}
            onChange={(event) => setDocumentTitle(event.target.value)}
            error={titleError}
            helperText={titleError ? "Le titre du document est obligatoire." : " "}
            disabled={createDocumentMutation.isLoading}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeCreateModal} variant="outlined" disabled={createDocumentMutation.isLoading}>
            Annuler
          </Button>
          <Button onClick={createFreeFormDocument} variant="contained" color="secondary" disabled={createDocumentMutation.isLoading}>
            {createDocumentMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : "Ajouter"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FreeFormDocuments;
